import { Avatar, Button, Checkbox, Icon, Text } from "@assetwatch/ui";
import { useEffect, useState } from "react";
import type { WorkRequestId } from "../../../lib/workIds";
import type {
  WorkRequest,
  WorkRequestStatus,
} from "../../../types/workRequest";
import { PageViewEmptyState } from "../../page-views/PageViewStates";
import { PriorityIndicator } from "../PriorityIndicator";
import { UnreadIndicator } from "../UnreadIndicator";
import { WorkListStatusChip } from "../WorkListStatusChip";

type RequestQueueProps = {
  canDeleteRequests: boolean;
  hasNextPage?: boolean;
  isFetchNextPageError?: boolean;
  isFetchingNextPage?: boolean;
  pendingRequests: WorkRequest[];
  resolvedRequests: WorkRequest[];
  selectedRequestId?: WorkRequestId;
  onDeleteSelected: (requestIds: WorkRequestId[]) => void;
  aiInsightRequestIds?: Set<string>;
  onSelect: (requestId: WorkRequestId) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onShowMore?: () => void;
};

type RequestListProps = {
  canDeleteRequests: boolean;
  onSelect: (requestId: WorkRequestId) => void;
  onToggleSelected: (requestId: WorkRequestId, checked: boolean) => void;
  aiInsightRequestIds?: Set<string>;
  requests: WorkRequest[];
  selectedRequestId?: WorkRequestId;
  selectedRequestIds: Set<WorkRequestId>;
};

export function requestStatusTone(
  code: WorkRequestStatus | "COMPLETED",
): "positive" | "informative" | "neutral" | "negative" {
  if (code === "COMPLETED") return "positive";
  if (code === "APPROVED") return "informative";
  if (code === "PENDING") return "neutral";
  if (code === "CANCELED" || code === "DELETED") return "neutral";
  return "negative";
}

/**
 * The API returns request numbers already prefixed ("WR-123"); the card shows
 * the bare number with a single "#" ("#123").
 */
function formatRequestCardNumber(requestNumber: string): string {
  return `#${requestNumber.replace(/^WR-/i, "")}`;
}

function getInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();
}

type RequestRowProps = {
  canDeleteRequests: boolean;
  hasAiInsight: boolean;
  isActive: boolean;
  isChecked: boolean;
  onSelect: (requestId: WorkRequestId) => void;
  onToggleSelected: (requestId: WorkRequestId, checked: boolean) => void;
  request: WorkRequest;
};

function RequestRow({
  canDeleteRequests,
  isActive,
  isChecked,
  onSelect,
  onToggleSelected,
  request,
}: RequestRowProps) {
  const requestNumber = formatRequestCardNumber(request.requestNumber);
  // The POC's mock data lets bulk-select/delete apply to Pending, Approved,
  // and Declined alike (every non-requester-visible group) because its
  // "delete" is just a client-side array filter with no real status
  // constraint. The production delete endpoint is narrower: it flag-deletes
  // (soft-tombstones, restorable by an administrator) and only transitions a
  // request out of DECLINED - see `deleteWorkRequest`/`transitionWorkRequest`
  // (`apps/work/backend/src/work-requests/mutations.ts`), which matches the
  // row by `sourceStatusCode: "DECLINED"` and 404s/409s for any other status,
  // including PENDING, CANCELED, and APPROVED. Widening this set to those
  // statuses would show a checkbox whose "Delete n" action silently fails
  // server-side, so bulk-select stays DECLINED-only until a broader backend
  // delete capability exists.
  const selectableStatuses: WorkRequestStatus[] = ["DECLINED"];
  const canDelete = selectableStatuses.includes(request.status.code);
  const canToggleSelect = canDeleteRequests && canDelete;
  const assetMetadata = request.asset?.name ?? "Unassigned";

  return (
    <li
      className={`group relative border-b border-outline-subtle transition-colors ${
        isActive ? "bg-primary/8" : "bg-surface hover:bg-surface-hover"
      }`}
    >
      {isActive ? (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-(--spacing-2) bg-primary"
          data-slot="request-selection-bar"
        />
      ) : null}
      <div className="flex items-start gap-(--spacing-12) px-(--spacing-16) py-(--spacing-12)">
        {/* @ds-deviation: thumbnail + hover-to-select checkbox — no AWDS component
            models a record thumbnail that swaps to a selection checkbox on
            hover/focus. Requests have no asset imagery yet, so every row
            shows a generic record icon that fades into the bulk-select
            checkbox on hover/focus for admin-eligible rows. The checkbox
            stays mounted at all times (never display:none) so it is always
            reachable by keyboard; only its opacity changes. Fix-in-product
            now, flagged for promotion. */}
        {/* @ds-deviation: the POC row icon is an `inbox` glyph, which the DS
            icon set does not include. Uses `checklist`, the icon the Work
            sidebar already uses for Requests, so the row and nav match.
            Promote into the design system: add an `inbox` icon. */}
        <span className="relative flex size-(--spacing-32) shrink-0 items-center justify-center rounded-(--radius-sm) bg-surface-container-low">
          {canToggleSelect ? (
            <>
              <span
                className={
                  isChecked
                    ? "opacity-0"
                    : "opacity-100 transition-opacity group-hover:opacity-0 group-focus-within:opacity-0 [@media(hover:none)]:opacity-0"
                }
              >
                <Icon aria-hidden name="checklist" size="sm" tone="secondary" />
              </span>
              <span
                className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                  isChecked
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
                }`}
              >
                <Checkbox
                  aria-label={`Select ${request.title}`}
                  checked={isChecked}
                  onCheckedChange={(checked: boolean) =>
                    onToggleSelected(request.id, Boolean(checked))
                  }
                />
              </span>
            </>
          ) : (
            <Icon aria-hidden name="checklist" size="sm" tone="secondary" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <button
            aria-label={`${request.isUnread ? "Unread, " : ""}${request.title}, requested by ${request.requester.name}, ${request.priority?.name ?? "unprioritized"}`}
            aria-pressed={isActive}
            className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button-focus-ring"
            onClick={() => onSelect(request.id)}
            type="button"
          >
            <span className="flex min-w-0 items-center justify-between gap-(--spacing-8)">
              <span className="flex min-w-0 items-center gap-(--spacing-8)">
                <span
                  className={`block min-w-0 truncate font-(family-name:--font-family-label) text-(length:--font-size-body-large) leading-(--font-line-height-20) text-text-default ${
                    request.isUnread
                      ? "font-(number:--font-weight-bold)"
                      : "font-(number:--font-weight-regular)"
                  }`}
                >
                  {request.title}
                </span>
                {request.isUnread ? (
                  <UnreadIndicator dataSlot="request-unread-indicator" />
                ) : null}
              </span>
              <Avatar
                initials={getInitials(request.requester.name)}
                size="xs"
                variant="external"
              />
            </span>
            <span className="mt-(--spacing-4) flex min-w-0 items-center justify-between gap-(--spacing-8) font-(family-name:--font-family-body) text-(length:--font-size-body-small) leading-(--font-line-height-16) text-text-subtle">
              <span className="min-w-0 truncate">{assetMetadata}</span>
              <span className="shrink-0 font-(family-name:--font-family-mono) tabular-nums">
                {requestNumber}
              </span>
            </span>
            <span className="mt-(--spacing-8) flex flex-wrap items-center justify-between gap-(--spacing-8)">
              <WorkListStatusChip
                label={request.status.name}
                tone={requestStatusTone(request.status.code)}
              />
              {request.priority ? (
                <PriorityIndicator priority={request.priority.code} />
              ) : null}
            </span>
          </button>
        </div>
      </div>
    </li>
  );
}

function RequestList({
  canDeleteRequests,
  aiInsightRequestIds,
  onSelect,
  onToggleSelected,
  requests,
  selectedRequestId,
  selectedRequestIds,
}: RequestListProps) {
  return (
    <ul>
      {requests.map((request) => (
        <RequestRow
          canDeleteRequests={canDeleteRequests}
          hasAiInsight={aiInsightRequestIds?.has(request.id) ?? false}
          isActive={request.id === selectedRequestId}
          isChecked={selectedRequestIds.has(request.id)}
          key={request.id}
          onSelect={onSelect}
          onToggleSelected={onToggleSelected}
          request={request}
        />
      ))}
    </ul>
  );
}
function RequestGroup({
  canDeleteRequests,
  aiInsightRequestIds,
  onSelect,
  onToggleSelected,
  requests,
  selectedRequestId,
  selectedRequestIds,
  title,
  defaultOpen,
  hasActiveFilters,
}: RequestListProps & {
  title: string;
  defaultOpen: boolean;
  hasActiveFilters: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  useEffect(() => {
    if (hasActiveFilters) setIsOpen(true);
  }, [hasActiveFilters]);

  return (
    <section className="bg-surface">
      <button
        aria-expanded={isOpen}
        className="flex min-h-(--spacing-40) w-full cursor-pointer items-center justify-between border-0 bg-surface px-(--spacing-16) py-(--spacing-10) text-left font-(family-name:--font-family-label) text-(length:--font-size-body-medium) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default outline-none hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button-focus-ring"
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <span>
          {title} ({requests.length})
        </span>
        <Icon
          name={isOpen ? "chevron-up" : "chevron-down"}
          size="sm"
          tone="secondary"
        />
      </button>
      {isOpen ? (
        <RequestList
          canDeleteRequests={canDeleteRequests}
          aiInsightRequestIds={aiInsightRequestIds}
          onSelect={onSelect}
          onToggleSelected={onToggleSelected}
          requests={requests}
          selectedRequestId={selectedRequestId}
          selectedRequestIds={selectedRequestIds}
        />
      ) : null}
    </section>
  );
}

export function RequestQueue({
  canDeleteRequests,
  hasNextPage = false,
  isFetchNextPageError = false,
  isFetchingNextPage = false,
  aiInsightRequestIds,
  onDeleteSelected,
  onSelect,
  hasActiveFilters,
  onClearFilters,
  onShowMore,
  pendingRequests,
  resolvedRequests,
  selectedRequestId,
}: RequestQueueProps) {
  const [selectedRequestIds, setSelectedRequestIds] = useState<
    Set<WorkRequestId>
  >(new Set());
  const approvedRequests = resolvedRequests.filter(
    (request) => request.status.code === "APPROVED",
  );
  const declinedRequests = resolvedRequests.filter((request) =>
    ["DECLINED", "CANCELED"].includes(request.status.code),
  );
  const deletedRequests = resolvedRequests.filter(
    (request) => request.status.code === "DELETED",
  );
  // The filtered flat list keeps the grouped view's status order (Deleted
  // last) so dropping the headers doesn't reshuffle the queue.
  const visibleRequests = [
    ...pendingRequests,
    ...approvedRequests,
    ...declinedRequests,
    ...deletedRequests,
  ];
  // Only DECLINED requests are deletable server-side (see the @ds-deviation-
  // adjacent comment in RequestRow above); "Done or Declined" is a display
  // grouping that also contains requester-canceled requests, which must stay
  // out of the deletable/selectable set.
  const deletableRequests = resolvedRequests.filter(
    (request) => request.status.code === "DECLINED",
  );

  useEffect(() => {
    const selectableIds = new Set(
      deletableRequests.map((request) => request.id),
    );
    setSelectedRequestIds((current) => {
      const next = new Set(
        [...current].filter((requestId) => selectableIds.has(requestId)),
      );
      return next.size === current.size ? current : next;
    });
  }, [deletableRequests]);

  function toggleSelected(requestId: WorkRequestId, checked: boolean) {
    setSelectedRequestIds((current) => {
      const next = new Set(current);
      if (checked) next.add(requestId);
      else next.delete(requestId);
      return next;
    });
  }

  function clearSelection() {
    setSelectedRequestIds(new Set());
  }

  function deleteSelected() {
    const deletableIds = new Set(
      deletableRequests.map((request) => request.id),
    );
    onDeleteSelected(
      [...selectedRequestIds].filter((requestId) =>
        deletableIds.has(requestId),
      ),
    );
    clearSelection();
  }

  return (
    <section
      aria-label="Work request queue"
      className="flex min-h-0 flex-col overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface"
    >
      {canDeleteRequests && selectedRequestIds.size > 0 ? (
        <div className="flex shrink-0 items-center justify-between border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12)">
          <Text size="sm">{selectedRequestIds.size} selected</Text>
          <div className="flex items-center gap-(--spacing-16)">
            <Button onClick={clearSelection} size="sm" variant="ghost">
              Clear
            </Button>
            {/* @ds-deviation: the POC shows the trash icon leading the label
                ("🗑 Delete n"); the DS Button's `icon` prop only renders an
                icon after the label. Using the DS icon slot (trailing) rather
                than hand-rolling a leading-icon button. Fix-in-product if/when
                Button grows a leading-icon option. */}
            <Button
              icon="trash-delete-bin"
              intent="destructive"
              onClick={deleteSelected}
              size="sm"
            >
              {`Delete ${selectedRequestIds.size}`}
            </Button>
          </div>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {visibleRequests.length === 0 ? (
          <PageViewEmptyState
            hasActiveFilters={hasActiveFilters}
            onClearFilters={hasActiveFilters ? onClearFilters : undefined}
          />
        ) : null}
        {visibleRequests.length > 0 ? (
          <>
            {pendingRequests.length ? (
              <RequestGroup
                canDeleteRequests={canDeleteRequests}
                aiInsightRequestIds={aiInsightRequestIds}
                defaultOpen
                onSelect={onSelect}
                onToggleSelected={toggleSelected}
                requests={pendingRequests}
                selectedRequestId={selectedRequestId}
                selectedRequestIds={selectedRequestIds}
                hasActiveFilters={hasActiveFilters}
                title="Pending"
              />
            ) : null}
            {approvedRequests.length ? (
              <RequestGroup
                canDeleteRequests={canDeleteRequests}
                aiInsightRequestIds={aiInsightRequestIds}
                defaultOpen
                onSelect={onSelect}
                onToggleSelected={toggleSelected}
                requests={approvedRequests}
                selectedRequestId={selectedRequestId}
                selectedRequestIds={selectedRequestIds}
                hasActiveFilters={hasActiveFilters}
                title="Approved"
              />
            ) : null}
            {declinedRequests.length ? (
              <RequestGroup
                canDeleteRequests={canDeleteRequests}
                aiInsightRequestIds={aiInsightRequestIds}
                defaultOpen={false}
                onSelect={onSelect}
                onToggleSelected={toggleSelected}
                requests={declinedRequests}
                selectedRequestId={selectedRequestId}
                selectedRequestIds={selectedRequestIds}
                hasActiveFilters={hasActiveFilters}
                title="Done or Declined"
              />
            ) : null}
            {deletedRequests.length ? (
              <RequestGroup
                canDeleteRequests={false}
                aiInsightRequestIds={aiInsightRequestIds}
                defaultOpen={false}
                onSelect={onSelect}
                onToggleSelected={toggleSelected}
                requests={deletedRequests}
                selectedRequestId={selectedRequestId}
                selectedRequestIds={selectedRequestIds}
                hasActiveFilters={hasActiveFilters}
                title="Deleted"
              />
            ) : null}
          </>
        ) : null}
      </div>
      {hasNextPage && onShowMore ? (
        <div className="flex shrink-0 flex-col items-center gap-(--spacing-8) px-(--spacing-16) py-(--spacing-16)">
          {isFetchNextPageError ? (
            <Text size="sm" tone="negative">
              Unable to load more requests.
            </Text>
          ) : null}
          <Button
            loading={isFetchingNextPage}
            loadingText="Loading"
            onClick={onShowMore}
            variant="outline"
          >
            Show more
          </Button>
        </div>
      ) : null}
    </section>
  );
}
